import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Linkedin, Mail, ChevronDown } from 'lucide-react';
import TerminalIntro from './components/TerminalIntro';

// Career Journey Component with Lazy Loading Animation
function CareerJourney() {
  const [visibleItems, setVisibleItems] = useState([]);
  const sectionRef = useRef(null);

  const journeyData = useMemo(() => [
    {
      year: '2022',
      title: 'Computer Science Graduate',
      subtitle: '@Graduation',
      period: 'June 2022',
      company: 'SATI Vidisha',
      icon: '🎓'
    },
    {
      year: '2022',
      title: 'Associate Software Engineer',
      subtitle: '@Yash Technologies',
      period: 'Jun 2022 - Feb 2024',
      company: 'Yash Technologies',
      icon: '👨‍💻'
    },
    {
      year: '2024',
      title: 'Software Engineer',
      subtitle: '@Techouts',
      period: 'March 2024 - Feb 2025',
      company: 'Techouts Inc',
      icon: '🚀'
    },
    {
      year: '2025',
      title: 'Software Engineer',
      subtitle: '@Keka Technologies',
      period: 'Feb 2025 - Present',
      company: 'Keka Technologies',
      icon: '💼'
    },
    {
      year: '2025',
      title: 'First Believer !!',
      subtitle: '@Flagtrace Inc',
      period: 'Oct 2025 - Present',
      company: 'Flagtrace Inc',
      icon: '🏢'
    }
  ], []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Start sequential animation
          journeyData.forEach((_, index) => {
            setTimeout(() => {
              setVisibleItems(prev => [...prev, index]);
            }, index * 800); // 800ms delay between each item
          });
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [journeyData]);

  return (
    <>
      {/* Add CSS Animation */}
      <style>
        {`
          @keyframes moveArrow {
            0% {
              background-position: 0px 0px;
            }
            100% {
              background-position: 32px 0px;
            }
          }
          

        `}
      </style>
      
      <section 
        ref={sectionRef}
        style={{
          padding: '4rem 2rem',
          background: 'rgba(0,0,0,0.3)',
          backdropFilter: 'blur(10px)'
        }}
      >
      <div style={{ maxWidth: '1200px', margin: '0 auto', color: 'white' }}>
        <h2 style={{
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          textAlign: 'center',
          marginBottom: '4rem',
          background: 'linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 25%, #45b7d1 50%, #96ceb4 75%, #feca57 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'subtleFloat 8s ease-in-out infinite',
          textShadow: '0 4px 8px rgba(0,0,0,0.2)',
          fontWeight: '700'
        }}>
          Career Journey
        </h2>

        {/* Timeline Container */}
        <div className="career-timeline" style={{ 
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          overflowX: 'auto',
          padding: '3rem 2rem',
          minHeight: '200px'
        }}>


          {journeyData.map((item, index) => (
            <div
              key={index}
              style={{
                flex: window.innerWidth <= 768 ? 'none' : '1',
                minWidth: window.innerWidth <= 768 ? '100%' : '180px',
                maxWidth: window.innerWidth <= 768 ? '100%' : '220px',
                textAlign: 'center',
                position: 'relative',
                zIndex: 2,
                transform: visibleItems.includes(index) ? 'translateY(0)' : 'translateY(50px)',
                opacity: visibleItems.includes(index) ? 1 : 0,
                transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                transitionDelay: visibleItems.includes(index) ? '0s' : `${index * 0.2}s`,
                margin: window.innerWidth <= 768 ? '1rem 0' : '0 10px',
                padding: window.innerWidth <= 768 ? '1.5rem' : '0',
                background: window.innerWidth <= 768 ? 'rgba(255,255,255,0.05)' : 'transparent',
                borderRadius: window.innerWidth <= 768 ? '12px' : '0',
                border: window.innerWidth <= 768 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                backdropFilter: window.innerWidth <= 768 ? 'blur(10px)' : 'none'
              }}
            >
              {/* Timeline Dot */}
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: visibleItems.includes(index) 
                  ? 'linear-gradient(135deg, #8b45db 0%, #b794f6 100%)'
                  : 'rgba(139, 69, 219, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '1.5rem',
                border: '3px solid rgba(255, 255, 255, 0.2)',
                boxShadow: visibleItems.includes(index) ? '0 0 20px rgba(139, 69, 219, 0.5)' : 'none',
                transition: 'all 0.8s ease',
                zIndex: 3,
                position: 'relative',
                transform: visibleItems.includes(index) ? 'scale(1)' : 'scale(0.8)',
                marginTop: '0'
              }}>
                {item.icon}
              </div>

              {/* Year */}
              <div style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#8b45db',
                marginBottom: '0.5rem'
              }}>
                {item.year}
              </div>

              {/* Job Title */}
              <h3 style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: 'white',
                marginBottom: '0.25rem',
                lineHeight: '1.3'
              }}>
                {item.title}
              </h3>

              {/* Company */}
              <p style={{
                fontSize: '0.9rem',
                color: '#b794f6',
                marginBottom: '0.5rem'
              }}>
                {item.subtitle}
              </p>

              {/* Period */}
              <p style={{
                fontSize: '0.8rem',
                color: 'rgba(255, 255, 255, 0.7)',
                lineHeight: '1.2'
              }}>
                {item.period}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
    </>
  );
}

function ModernPortfolio() {
  const [hoveredTech, setHoveredTech] = useState(null);
  const [showTerminalIntro, setShowTerminalIntro] = useState(true);
  const [showMainContent, setShowMainContent] = useState(false);
  const techRef = useRef(null);

  const techStack = [
    { name: 'React', icon: '⚛️', color: '#61DAFB' },
    { name: 'HTML5', icon: '🌐', color: '#E34F26' },
    { name: 'CSS3', icon: '🎨', color: '#1572B6' },
    { name: 'JavaScript', icon: 'JS', color: '#F7DF1E' },
    { name: '.NET Framework', icon: '🔷', color: '#512BD4' },
    { name: 'Microsoft Azure', icon: '☁️', color: '#0078D4' },
    { name: 'Git', icon: '🔀', color: '#F05032' },
    { name: 'GitHub', icon: '🐙', color: '#181717' },
    { name: 'SQL Server', icon: '🗄️', color: '#CC2927' },
    { name: 'Power BI', icon: '📊', color: '#F2C811' },
    { name: 'Angular', icon: '🅰️', color: '#DD0031' },
    { name: 'Docker', icon: '🐳', color: '#2496ED' },
    { name: 'Artificial Intelligence', icon: '🤖', color: '#00D4AA' }
  ];

  return (
    <>
      {showTerminalIntro && (
        <TerminalIntro onComplete={() => {
          setShowTerminalIntro(false);
          setTimeout(() => setShowMainContent(true), 200);
        }} />
      )}
      
      <style>
        {`
          @keyframes tooltipFadeIn {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(5px);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0);
            }
          }
          
          @keyframes gradientShift {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }
          
          @keyframes floatingOrbs {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
              opacity: 0.7;
            }
            50% {
              transform: translateY(-20px) rotate(180deg);
              opacity: 1;
            }
          }
          
          @keyframes subtleFloat {
            0%, 100% {
              transform: translateY(0px);
            }
            50% {
              transform: translateY(-10px);
            }
          }
          
          @keyframes colorPulse {
            0%, 100% {
              filter: hue-rotate(0deg) brightness(1);
            }
            50% {
              filter: hue-rotate(20deg) brightness(1.1);
            }
          }
          
          @keyframes shimmer {
            0% {
              background-position: -200% center;
            }
            100% {
              background-position: 200% center;
            }
          }
          
          @keyframes starTwinkle {
            0%, 100% {
              opacity: 0.3;
              transform: scale(1);
            }
            50% {
              opacity: 1;
              transform: scale(1.2);
            }
          }
          
          @keyframes galaxyRotate {
            0% {
              transform: rotate(0deg) scale(1);
              opacity: 0.2;
            }
            50% {
              opacity: 0.4;
            }
            100% {
              transform: rotate(360deg) scale(1);
              opacity: 0.2;
            }
          }
          
          @keyframes milkyWayFlow {
            0% {
              transform: translateX(-100%) rotate(-10deg);
              opacity: 0;
            }
            20% {
              opacity: 0.3;
            }
            80% {
              opacity: 0.3;
            }
            100% {
              transform: translateX(100vw) rotate(10deg);
              opacity: 0;
            }
          }
          
          @keyframes nebulaFloat {
            0%, 100% {
              transform: translate(0, 0) rotate(0deg);
              opacity: 0.1;
            }
            33% {
              transform: translate(30px, -20px) rotate(120deg);
              opacity: 0.3;
            }
            66% {
              transform: translate(-20px, 30px) rotate(240deg);
              opacity: 0.2;
            }
          }
          
          @keyframes fadeInFromBlur {
            0% {
              opacity: 0;
              filter: blur(20px);
              transform: scale(1.05);
            }
            100% {
              opacity: 1;
              filter: blur(0px);
              transform: scale(1);
            }
          }
        `}
      </style>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(-45deg, #667eea, #764ba2, #f093fb, #f5576c)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        position: 'relative',
        overflow: 'hidden',
        opacity: showTerminalIntro ? 0 : (showMainContent ? 1 : 0),
        filter: showTerminalIntro ? 'blur(15px)' : (showMainContent ? 'blur(0px)' : 'blur(10px)'),
        transition: 'opacity 2s ease-out, filter 2s ease-out'
      }}>
        {/* Galaxy and Milky Way Background */}
        
        {/* Stars Layer */}
        {[...Array(50)].map((_, i) => (
          <div
            key={`star-${i}`}
            style={{
              position: 'absolute',
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 3 + 1}px`,
              height: `${Math.random() * 3 + 1}px`,
              background: '#ffffff',
              borderRadius: '50%',
              animation: `starTwinkle ${Math.random() * 3 + 2}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              zIndex: 1,
              boxShadow: '0 0 6px rgba(255,255,255,0.8)'
            }}
          />
        ))}
        
        {/* Galaxy Spiral */}
        <div style={{
          position: 'absolute',
          top: '20%',
          right: '5%',
          width: '300px',
          height: '300px',
          background: `
            radial-gradient(ellipse 60% 20% at 50% 50%, 
              rgba(138, 43, 226, 0.4) 0%, 
              rgba(75, 0, 130, 0.3) 30%, 
              rgba(25, 25, 112, 0.2) 60%, 
              transparent 100%)
          `,
          borderRadius: '50%',
          animation: 'galaxyRotate 120s linear infinite',
          zIndex: 1,
          filter: 'blur(2px)',
          transform: 'rotate(-30deg)'
        }} />
        
        {/* Milky Way Stream */}
        <div style={{
          position: 'absolute',
          top: '0',
          left: '-20%',
          width: '140%',
          height: '100%',
          background: `
            linear-gradient(25deg, 
              transparent 0%, 
              rgba(147, 112, 219, 0.1) 20%,
              rgba(138, 43, 226, 0.2) 40%,
              rgba(75, 0, 130, 0.3) 50%,
              rgba(138, 43, 226, 0.2) 60%,
              rgba(147, 112, 219, 0.1) 80%,
              transparent 100%)
          `,
          animation: 'milkyWayFlow 60s linear infinite',
          zIndex: 1,
          filter: 'blur(3px)'
        }} />
        
        {/* Nebula Clouds */}
        <div style={{
          position: 'absolute',
          top: '70%',
          left: '10%',
          width: '200px',
          height: '150px',
          background: `
            radial-gradient(ellipse 80% 60% at 40% 60%, 
              rgba(255, 20, 147, 0.3) 0%, 
              rgba(138, 43, 226, 0.2) 40%, 
              transparent 70%)
          `,
          borderRadius: '50%',
          animation: 'nebulaFloat 40s ease-in-out infinite',
          zIndex: 1,
          filter: 'blur(4px)'
        }} />
        
        <div style={{
          position: 'absolute',
          top: '40%',
          left: '70%',
          width: '180px',
          height: '120px',
          background: `
            radial-gradient(ellipse 70% 50% at 60% 40%, 
              rgba(0, 191, 255, 0.3) 0%, 
              rgba(138, 43, 226, 0.2) 50%, 
              transparent 80%)
          `,
          borderRadius: '50%',
          animation: 'nebulaFloat 35s ease-in-out infinite reverse',
          animationDelay: '10s',
          zIndex: 1,
          filter: 'blur(3px)'
        }} />
        
        {/* Distant Galaxy */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '100px',
          height: '100px',
          background: `
            radial-gradient(circle, 
              rgba(255, 215, 0, 0.4) 0%, 
              rgba(255, 140, 0, 0.3) 30%, 
              rgba(220, 20, 60, 0.2) 60%, 
              transparent 100%)
          `,
          borderRadius: '50%',
          animation: 'galaxyRotate 80s linear infinite reverse',
          zIndex: 1,
          filter: 'blur(2px)'
        }} />
        
      {/* Hero Section */}
      <section id="home" className="mobile-section" style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '2rem',
        position: 'relative'
      }}>
        <div className="mobile-container" style={{ 
          maxWidth: '800px', 
          color: 'white',
          width: '100%'
        }}>
          {/* Profile Photo */}
          <div style={{ 
            position: 'relative', 
            display: 'inline-block', 
            marginBottom: '2rem' 
          }}>
            <div style={{
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              overflow: 'hidden',
              margin: '0 auto',
              position: 'relative',
              boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
              border: '4px solid rgba(255,255,255,0.2)'
            }}>
              <img 
                src="/profile photo.jpeg" 
                alt="Abhay Kumar Singh" 
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                onError={(e) => {
                  // Fallback if image doesn't load
                  e.target.style.display = 'none';
                  e.target.parentElement.innerHTML = `
                    <div style="
                      width: 100%;
                      height: 100%;
                      background: linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 50%, #45b7d1 100%);
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      font-size: 3rem;
                      font-weight: bold;
                      color: white;
                    ">AK</div>
                  `;
                }}
              />
            </div>
          </div>

          {/* Name and Title */}
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: '900',
            marginBottom: '0.8rem',
            color: '#ffffff',
            textShadow: '0 4px 12px rgba(0,0,0,0.5), 0 2px 4px rgba(78, 205, 196, 0.3)',
            letterSpacing: '0.5px'
          }}>
            Abhay Kumar Singh
          </h1>

          {/* Punchy Tagline */}
          <div style={{
            fontSize: 'clamp(1.1rem, 2vw, 1.6rem)',
            fontWeight: '500',
            marginBottom: '1.5rem',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #4ecdc4 30%, #45b7d1 60%, #96ceb4 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 2px 6px rgba(0,0,0,0.2)',
            letterSpacing: '2px',
            animation: 'subtleFloat 6s ease-in-out infinite',
            textTransform: 'uppercase',
            opacity: '0.9'
          }}>
            Build Beyond Limits
          </div>

          {/* Inspiring Quote */}
          <p style={{
            fontSize: 'clamp(1.1rem, 1.8vw, 1.4rem)',
            fontWeight: '400',
            marginBottom: '3rem',
            color: 'rgba(255,255,255,0.95)',
            maxWidth: '650px',
            margin: '0 auto 3rem auto',
            lineHeight: '1.5',
            fontStyle: 'italic',
            letterSpacing: '0.3px'
          }}>
            "Curiosity drives me. Vision guides me. Impact defines me."
          </p>

          {/* Action Buttons */}
          <div className="action-buttons" style={{ 
            display: 'flex', 
            gap: '1rem', 
            justifyContent: 'center',
            marginBottom: '3rem',
            flexWrap: 'wrap',
            padding: '0 1rem'
          }}>
            <a
              href="https://www.linkedin.com/in/abhayksingh2308"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                // Ensure navigation works
                window.open('https://www.linkedin.com/in/abhayksingh2308', '_blank');
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '12px 24px',
                background: '#0077b5',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
                fontWeight: '500',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 15px rgba(0,119,181,0.3)',
                cursor: 'pointer',
                zIndex: 10,
                position: 'relative',
                minHeight: '44px',
                minWidth: window.innerWidth <= 768 ? 'auto' : 'auto'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,119,181,0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,119,181,0.3)';
              }}
            >
              <Linkedin size={20} />
              LinkedIn
            </a>
            <a
              href="mailto:abhayksingh23@gmail.com"
              onClick={(e) => {
                // Ensure mailto works
                window.location.href = 'mailto:abhayksingh23@gmail.com';
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: window.innerWidth <= 768 ? '14px 20px' : '12px 24px',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '8px',
                fontSize: window.innerWidth <= 768 ? '0.9rem' : '1rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)',
                textDecoration: 'none',
                zIndex: 10,
                position: 'relative',
                minHeight: '44px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Mail size={20} />
              Get In Touch
            </a>
          </div>

          {/* Tech Stack */}
          <div style={{ marginBottom: '3rem' }}>
            <p style={{ 
              color: 'rgba(255,255,255,0.8)', 
              marginBottom: '1.5rem',
              fontSize: '0.9rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              textAlign: 'center'
            }}>
              Tech Stack
            </p>

            <div style={{ position: 'relative', maxWidth: '100%', margin: '0 auto', padding: '0 1rem' }}>
              {/* Left Arrow */}
              <button
                onClick={() => {
                  if (techRef.current) {
                    techRef.current.scrollBy({ left: -Math.round(techRef.current.clientWidth / 2), behavior: 'smooth' });
                  }
                }}
                aria-label="Scroll left"
                style={{
                  position: 'absolute',
                  left: '6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 20,
                  background: 'rgba(0,0,0,0.45)',
                  border: 'none',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                ‹
              </button>

              <div
                ref={techRef}
                style={{
                  display: 'flex',
                  gap: '0.8rem',
                  overflowX: 'auto',
                  padding: '0.5rem 2.5rem',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                  scrollBehavior: 'smooth'
                }}
                className="tech-stack-container"
              >
                {techStack.map((tech, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: '0 0 72px',
                      width: '72px',
                      height: '72px',
                      background: hoveredTech === index ? 'rgba(255, 107, 107, 0.9)' : 'rgba(255, 255, 255, 0.12)',
                      borderRadius: '14px',
                      backdropFilter: 'blur(10px)',
                      border: `2px solid ${hoveredTech === index ? 'rgba(78, 205, 196, 1)' : 'rgba(255, 255, 255, 0.18)'}`,
                      boxShadow: hoveredTech === index ? '0 8px 32px rgba(78, 205, 196, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.12)',
                      transition: 'all 0.24s ease',
                      cursor: 'pointer',
                      transform: hoveredTech === index ? 'translateY(-4px) scale(1.05)' : 'translateY(0) scale(1)'
                    }}
                    onMouseEnter={() => setHoveredTech(index)}
                    onMouseLeave={() => setHoveredTech(null)}
                  >
                    <span style={{ 
                      fontSize: tech.icon.length > 2 ? '0.78rem' : '1.6rem',
                      fontWeight: tech.icon.length > 2 ? '700' : '400',
                      color: 'rgba(255, 255, 255, 0.95)'
                    }}>
                      {tech.icon}
                    </span>

                    {/* Tooltip */}
                    {hoveredTech === index && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '-62px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: 'rgba(0, 0, 0, 0.95)',
                          color: 'white',
                          padding: '10px 14px',
                          borderRadius: '8px',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          whiteSpace: 'nowrap',
                          zIndex: 10000,
                          boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          animation: 'tooltipFadeIn 0.18s ease-out'
                        }}
                      >
                        {tech.name}
                        {/* Arrow */}
                        <div style={{
                          position: 'absolute',
                          bottom: '-6px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: 0,
                          height: 0,
                          borderLeft: '6px solid transparent',
                          borderRight: '6px solid transparent',
                          borderTop: '6px solid rgba(0, 0, 0, 0.95)'
                        }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => {
                  if (techRef.current) {
                    techRef.current.scrollBy({ left: Math.round(techRef.current.clientWidth / 2), behavior: 'smooth' });
                  }
                }}
                aria-label="Scroll right"
                style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 20,
                  background: 'rgba(0,0,0,0.45)',
                  border: 'none',
                  color: 'white',
                  padding: '8px',
                  borderRadius: '8px',
                  cursor: 'pointer'
                }}
              >
                ›
              </button>
            </div>
          </div>

          {/* Scroll Down Indicator */}
          <div
            style={{
              position: 'absolute',
              bottom: '2rem',
              left: '50%',
              transform: 'translateX(-50%)',
              animation: 'bounce 2s infinite',
              cursor: 'pointer',
              zIndex: 10
            }}
            onClick={() => {
              document.getElementById('about')?.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start' 
              });
            }}
          >
            <ChevronDown 
              size={32} 
              style={{ 
                color: 'rgba(255,255,255,0.8)',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }} 
            />
          </div>
        </div>
      </section>

      {/* About Me Section */}
      <section id="about" className="mobile-section" style={{
        padding: '4rem 2rem',
        background: 'rgba(0,0,0,0.2)',
        backdropFilter: 'blur(10px)'
      }}>
        <div className="mobile-container" style={{ 
          maxWidth: '800px', 
          margin: '0 auto', 
          color: 'white' 
        }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            textAlign: 'center',
            marginBottom: '3rem',
            background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            About Me
          </h2>
          
          <div style={{ 
            fontSize: '1.2rem', 
            lineHeight: '1.8', 
            textAlign: 'left',
            color: 'rgba(255,255,255,0.9)'
          }}>
            <p style={{ marginBottom: '1.5rem' }}>
              I'm a passionate <span style={{ color: '#00d4ff', fontWeight: '600' }}>Software Engineer</span> with 4+ years of experience 
              specializing in building enterprise-level applications using <span style={{ color: '#00d4ff' }}>.NET, Angular, and Azure Cloud</span>.
            </p>
            
            <p style={{ marginBottom: '1.5rem' }}>
              My expertise lies in architecting <span style={{ color: '#4ecdc4' }}>microservices</span>, optimizing cloud 
              infrastructure, and delivering high-performance solutions. I've successfully reduced Azure costs by 70% and built scalable 
              APIs serving thousands of users.
            </p>
            
            <p style={{ marginBottom: '0' }}>
              I hold multiple Azure certifications including <span style={{ color: '#00d4ff' }}>AZ-204 (Developer Associate)</span> and <span style={{ color: '#00d4ff' }}>AZ-104 (Administrator)</span>, demonstrating my 
              commitment to cloud excellence.
            </p>
          </div>
        </div>
      </section>

      {/* Career Journey Section */}
      <CareerJourney />

      {/* Contact Section */}
      <section id="contact" className="mobile-section" style={{
        minHeight: '50vh',
        padding: '4rem 2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="mobile-container" style={{ 
          maxWidth: '600px', 
          textAlign: 'center', 
          color: 'white',
          width: '100%'
        }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: '2rem',
            background: 'linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>
            Let's Connect
          </h2>
          <p style={{ 
            fontSize: '1.2rem', 
            marginBottom: '2rem',
            color: 'rgba(255,255,255,0.8)'
          }}>
            I'm always open to discussing new projects and opportunities.
          </p>
          
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="mailto:AbhayKsingh227@gmail.com"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '12px 24px',
                background: 'rgba(255,255,255,0.1)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '8px',
                border: '2px solid rgba(255,255,255,0.3)',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <Mail size={20} />
              AbhayKsingh227@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* Add bounce animation and mobile styles */}
      <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateX(-50%) translateY(0);
          }
          40% {
            transform: translateX(-50%) translateY(-10px);
          }
          60% {
            transform: translateX(-50%) translateY(-5px);
          }
        }
        
        .tech-stack-container {
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
        }
        
        .tech-stack-container::-webkit-scrollbar {
          display: none;
        }
        
        /* Mobile responsive styles */
        @media (max-width: 768px) {
          /* Hero section mobile */
          #home {
            padding: 1rem !important;
            min-height: 100vh !important;
          }
          
          /* Tech stack mobile scrolling */
          .tech-stack-container {
            justify-content: flex-start !important;
            padding-bottom: 10px !important;
            gap: 0.8rem !important;
          }
          
          /* Action buttons mobile */
          .action-buttons {
            gap: 0.8rem !important;
          }
          
          /* Buttons mobile friendly */
          .mobile-btn {
            padding: 14px 20px !important;
            font-size: 0.9rem !important;
            min-height: 44px !important;
          }
          
          /* Career journey mobile */
          .career-timeline {
            flex-direction: column !important;
            gap: 2rem !important;
            padding: 2rem 1rem !important;
            align-items: center !important;
            justify-content: center !important;
          }
          

          
          /* Section padding mobile */
          .mobile-section {
            padding: 3rem 1rem !important;
          }
          
          /* Container width mobile */
          .mobile-container {
            max-width: 90% !important;
            width: 100% !important;
          }
        }
        
        /* Tablet styles */
        @media (max-width: 1024px) and (min-width: 769px) {
          .tech-stack-container {
            flex-wrap: wrap !important;
            justify-content: center !important;
          }
        }
        
        /* Ensure proper responsive font sizes */
        @media (max-width: 480px) {
          h1 {
            font-size: 2rem !important;
          }
          h2 {
            font-size: 1.8rem !important;
          }
        }
      `}</style>
    </div>
    </>
  );
}

export default ModernPortfolio;